import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Select, Button, Row, Col, DatePicker, InputNumber, message, Spin, Input } from 'antd';
import { getCustomerSubscriptions } from '../service';
import { getProducts } from '../../product/service';
import { getActiveCustomers } from '../../customer/service';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import useSubscriptionState from '../hooks/useSubscriptionState';
import { useNavigate } from 'react-router-dom';
import { TERM_DATA } from '../constants';

export default function SubscriptionForm({ mode = 'add' }) {
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState({});
  const [subscriptions, setSubscriptions] = useState([]);
  const [customersList, setCustomersList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState({});
  const { Option } = Select;
  const { addOrUpdate } = useSubscriptionState();
  const [response, setResponse] = useState();
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  useEffect(() => {
    getProducts().then(data => {
      setProductsList(data);
    });
    if (mode === 'edit' && id) {
      getCustomerSubscriptionsData(id);
    } else {
      setLoadingCustomers(true);
      getActiveCustomers().then(data => {
        setCustomersList(data);
      }).catch(err => {
        messageApi.error('Failed to fetch customers', 5);
      }).finally(() => {
        setLoadingCustomers(false);
      });
    }
  }, [id, mode, form])

  useEffect(() => {
    if (mode === 'edit') {
      form.setFieldValue('customer_id', selectedCustomer)
    }
  }, [selectedCustomer])

  useEffect(() => {
    form.setFieldValue('subscription_items', subscriptions)
  }, [subscriptions])

  const getCustomerSubscriptionsData = (id) => {
      setLoading(true);
      getCustomerSubscriptions(id).then(data => {
        setLoading(false);
        const dateFormattedSubscriptions = (data.subscription_items || []).map(item => ({
          ...item,
          unit_price: +item.unit_price,
          discount_percentage: +item.discount_percentage,
          discount_amount: +item.discount_amount,
          amount: +item.amount,
          start_date: dayjs(item.start_date, 'YYYY-MM-DD'),
          end_date: dayjs(item.end_date, 'YYYY-MM-DD'),
          is_credit_memo: item.product.is_credit_memo,
        }));
        setSubscriptions(dateFormattedSubscriptions);
        if (dateFormattedSubscriptions.length > 0) {
          setCustomer(data.customer);
          setSelectedCustomer(data.customer.id);
          if (mode === 'edit') {
            setCustomersList([data.customer]);
          }
        } else {
          setCustomer({});
          setSelectedCustomer(id);
        }
      });
  }

  const onFinish = async (values) => {
    const requestData = {
      customer_id: values.customer_id,
      subscription_items: values.subscription_items.map(item => ({
        ...item,
        start_date: item.start_date?.format('YYYY-MM-DD'),
        end_date: item.end_date?.format('YYYY-MM-DD'),
      }))
    }
    const responseData = await addOrUpdate(requestData);
    if (!responseData?.success) {
      setResponse(responseData);
    } else {
      const toastMessage = mode === 'add' ?
        'Subscription added successfully' :
        'Subscription updated successfully';
      await messageApi.success(toastMessage, 2);
      navigate('/subscriptions');
    }
  }

  const handleValueChanges = (_, subscriptionsData) => {
    const updated = subscriptionsData.subscription_items?.map((item = {}) => {
      let unit = 0;
      let itemProduct = {};
      productsList.map(p => {
        if (+p.id === +item.product_id) {
          unit = +p.price;
          itemProduct = p;
        }
      })
      const percent = parseFloat(item.discount_percentage || 0);
      const fixed = parseFloat(item.discount_amount || 0);

      let discountAmount = fixed;
      let discountPercentage = percent;

      if (unit > 0) {
        discountAmount = (unit * percent) / 100;
      }

      const amount = unit - discountAmount;

      return {
        ...item,
        unit_price: unit.toFixed(2),
        discount_amount: discountAmount.toFixed(2),
        discount_percentage: discountPercentage.toFixed(2),
        amount: amount.toFixed(2),
        is_credit_memo: itemProduct.is_credit_memo,
      }
    });

    form.setFieldValue('subscription_items', updated)
  };

  return (
    <Form form={form} onValuesChange={handleValueChanges} layout="vertical" onFinish={onFinish} onChange={() => setResponse({})}>
      {contextHolder}
      <Row align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <label style={{ marginRight: 8 }}>Select Customer:</label>
        </Col>
        <Col span={6}>
          <Form.Item
            name="customer_id"
            noStyle
            rules={[{ required: true, message: 'Please select a customer' }]}
          >
            <Select
              placeholder="Select a Customer"
              notFoundContent="No customer available"
              onChange={(value) => getCustomerSubscriptionsData(value)}
              style={{ width: '250px'}}
              showSearch
              optionFilterProp='children'
              disabled={loadingCustomers}
            >
              {customersList?.length > 0 && customersList.map((c) => {
                return <Option key={c.id} value={c.id}>{c.name} ({c.code})</Option>
              })}
            </Select>
          </Form.Item>
          <Spin spinning={loadingCustomers} size='small' />
        </Col>
      </Row>
      <Form.List name="subscription_items" initialValue={[{}]}>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }, index) => {
              let isActive = true;
              if (subscriptions[index] && Object.prototype.hasOwnProperty.call(subscriptions[index], 'is_active')) {
                isActive = subscriptions[index].is_active;
              }
              const formProduct = form.getFieldValue('subscription_items');
              const isCreditMemo = formProduct[index].is_credit_memo || false;
              return (
                <>
                  <Row key={key} gutter={16} align="middle">
                    <Col span={4}>
                      <Form.Item
                        name={[name, 'product_id']}
                        label='Product'
                        rules={[{ required: true, message: 'Please select a product' }]}
                      >
                        <Select
                          placeholder="Select a Product"
                          notFoundContent="No product available"
                          style={{ width: '100%'}}
                          showSearch
                          optionFilterProp='children'
                          disabled={!isActive}
                        >
                          {productsList?.length > 0 && productsList.map((p) => {
                            return <Option key={p.id} value={p.id}>{p.name} {p.sku ? `(${p.sku})` : ''} {p.is_bundled ? ' - Bundle' : ''} {!p.is_active ? '- Inactive' : ''}</Option>
                          })}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item
                        {...restField}
                        name={[name, 'start_date']}
                        label='Start Date'
                        rules={[
                          { required: true, message: 'Start date is required' },
                        ]}
                      >
                        <DatePicker 
                          placeholder="Enter start date" 
                          format='MM/DD/YYYY' 
                          style={{ width: '100%' }}
                          disabled={!isActive}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item
                        {...restField}
                        name={[name, 'end_date']}
                        label='End Date'
                        rules={[
                          { required: true, message: 'End date is required' },
                        ]}
                      >
                        <DatePicker 
                          placeholder="Enter end date" 
                          format='MM/DD/YYYY' 
                          style={{ width: '100%' }}
                          disabled={!isActive}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item
                        name={[name, 'term']}
                        label='Term'
                        rules={[{ required: true, message: 'Please select a term' }]}
                      >
                        <Select
                          placeholder="Select a Term"
                          notFoundContent="No term available"
                          style={{ width: '100%'}}
                          showSearch
                          optionFilterProp='children'
                          disabled={!isActive}
                        >
                          {TERM_DATA?.length > 0 && TERM_DATA.map((p) => {
                            return <Option key={p.value} value={p.value}>{p.label}</Option>
                          })}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item
                        {...restField}
                        name={[name, 'invoice_due_days']}
                        label='Invoice Due Days'
                        rules={[
                          { required: true, message: 'Invoice Due Days are required' },
                          { type: 'number', transform: (value) => Number(value), message: 'Enter a valid number' },
                        ]}
                      >
                        <InputNumber style={{width: '100%'}} initialvalues={0} placeholder="Enter Invoice Due Days" disabled={!isActive}/>
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item
                        {...restField}
                        name={[name, 'unit_price']}
                        label='Original Price'
                        rules={[
                          { required: true, message: 'Original Price is required' },
                          { type: 'number', transform: (value) => Number(value), message: 'Enter a valid number' },
                        ]}
                      >
                        <InputNumber style={{width: '100%'}} readOnly min={0} step={0.01} initialvalues={0} placeholder="Enter Unit Price" disabled={!isActive} />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item
                        {...restField}
                        name={[name, 'discount_percentage']}
                        label='Discount (%)'
                        rules={[
                          { type: 'number', transform: (value) => Number(value), message: 'Enter a valid number' },
                        ]}
                      >
                        <InputNumber style={{width: '100%'}} min={0} max={100} step={0.01} initialvalues={0} placeholder="Enter Discount percentage" disabled={!isActive || isCreditMemo}/>
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item
                        {...restField}
                        name={[name, 'discount_amount']}
                        label='Discount Amount'
                        rules={[
                          { type: 'number', transform: (value) => Number(value), message: 'Enter a valid number' },
                        ]}
                      >
                        <InputNumber style={{width: '100%'}} readOnly min={0} step={0.01} initialvalues={0} placeholder="Enter Discount amount" disabled={!isActive || isCreditMemo}/>
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item
                        {...restField}
                        name={[name, 'amount']}
                        label='Sale Price'
                        rules={[
                          { required: true, message: 'Sale Price is required' },
                          { type: 'number', transform: (value) => Number(value), message: 'Enter a valid number' },
                        ]}
                      >
                        <InputNumber style={{width: '100%'}} readOnly min={0} step={0.01} initialvalues={0} placeholder="Enter Sale Price" disabled={!isActive}/>
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      {fields.length > 1 && (
                        <MinusCircleOutlined
                          onClick={() => remove(name)}
                          style={{ color: 'red', fontSize: 20 }}
                        />
                      )}
                    </Col>
                  </Row>
                </>
              )}
            )}
            <Form.Item>
              <Button
                onClick={() => add()}
                icon={<PlusOutlined />}
              >
                Assign Product
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
      {
        (!response?.success) && (
          <div style={{textAlign: 'center'}}>
            <label style={{color: 'red'}}>{response?.message} {response?.code ? `(${response?.code})` : ''}</label>
          </div>
        )
      }
      <Form.Item>
        <Button htmlType="submit" type="primary" style={{marginRight: "10px"}}>Save</Button>
      </Form.Item>
    </Form>
  );
}